import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PartnerHorizontalBarChart from './PartnerHorizontalBarChart';
import PartnerSingleLineChart from './PartnerSingleLineChart';
import PartnerStackedBarChart from './PartnerStackedBarChart';
import CompactDonutChart from './CompactDonutChart';
import RegionalIngestionOverview from './RegionalIngestionOverview';
import UserNarrativeStackedChart from './UserNarrativeStackedChart';
import SimpleVerticalBarChart from './SimpleVerticalBarChart';
import CompaniesModal from './CompaniesModal';
import ActivePartnerUsersModal from './ActivePartnerUsersModal';
import PartnerComparisonDropdown from './PartnerComparisonDropdown';
import {
  fetchLeadershipPartnerAnalytics,
  fetchLeadershipPartnerCompanyAnalytics,
} from '../../../../Redux/Constants/apiService';
import {
  createEmptyPartnerAnalytics,
  mapPartnerAnalyticsResponse,
  REGIONS,
  createEmptyPartnerCompanyAnalytics,
  mapPartnerCompanyAnalyticsResponse,
} from './partnerAnalyticsMapper';
import { PARTNER_CHART_PALETTE, PARTNER_BLUE_COLORS } from './chartTheme';

const OVERALL_COMPARE_OPTION = 'Overall';
const ALL_PARTNER_COMPANIES = 'All Partner Companies';
const PARTNER_REGION = 'all';
const WEEK_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FISCAL_MONTH_LABELS = [
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
  'Jan',
  'Feb',
  'Mar',
];

const PERIOD_FILTER_MAP = {
  Week: 'Week',
  LastWeek: 'LastWeek',
  Month: 'Month',
  LastMonth: 'LastMonth',
  Quarter: 'Quarter',
  LastQuarter: 'LastQuarter',
  Year: 'Year',
  LastYear: 'LastYear',
};

const FILTER_LABEL_MAP = {
  Week: 'Current week',
  LastWeek: 'Last week',
  Month: 'Current month',
  LastMonth: 'Last month',
  Quarter: 'Current quarter',
  LastQuarter: 'Last quarter',
  Year: 'Current year',
  LastYear: 'Last year',
};

const API_ERROR_MESSAGE = 'Unable to load analytics. Please try again.';
const areArraysEqual = (left = [], right = []) =>
  left.length === right.length && left.every((item, index) => item === right[index]);
const formatAverageRuns = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.round(numericValue);
};

const getQuarterMonthLabels = (period) => {
  const now = new Date();
  const month = now.getMonth();
  const currentQuarterStart = Math.floor(month / 3) * 3;
  const quarterStart = period === 'LastQuarter'
    ? (currentQuarterStart + 9) % 12
    : currentQuarterStart;
  return [0, 1, 2].map((offset) =>
    new Date(now.getFullYear(), quarterStart + offset, 1).toLocaleString('en-US', { month: 'short' })
  );
};

const getPeriodZeroBuckets = (period) => {
  if (period === 'Week' || period === 'LastWeek') return WEEK_DAY_LABELS;
  if (period === 'Quarter' || period === 'LastQuarter') return getQuarterMonthLabels(period);
  if (period === 'Year' || period === 'LastYear') return FISCAL_MONTH_LABELS;
  return ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
};

const normalizeTrendValues = (values, size) => {
  const normalized = new Array(size).fill(0);
  if (!Array.isArray(values)) return normalized;
  values.slice(0, size).forEach((value, index) => {
    const numeric = Number(value);
    normalized[index] = Number.isFinite(numeric) ? numeric : 0;
  });
  return normalized;
};

const ensureTrendChartData = ({
  trendData,
  period,
  fallbackXAxis = [],
  seriesNames = [],
}) => {
  const trendXAxis = Array.isArray(trendData?.xData) ? trendData.xData : [];
  const xData = trendXAxis.length
    ? trendXAxis
    : (fallbackXAxis.length ? fallbackXAxis : getPeriodZeroBuckets(period));

  const hasSeries = Array.isArray(trendData?.series) && trendData.series.length > 0;
  if (hasSeries) {
    return {
      ...trendData,
      xData,
      series: trendData.series.map((series) => ({
        ...series,
        data: normalizeTrendValues(series?.data, xData.length),
      })),
    };
  }

  if (Array.isArray(trendData?.data) && trendData.data.length > 0) {
    return {
      xData,
      data: normalizeTrendValues(trendData.data, xData.length),
    };
  }

  if (seriesNames.length) {
    return {
      xData,
      series: seriesNames.map((name) => ({
        name,
        data: new Array(xData.length).fill(0),
      })),
    };
  }

  return {
    xData,
    data: new Array(xData.length).fill(0),
  };
};

const PartnerRegionCard = ({ region, narratives, avgRuns }) => (
  <div className="col-lg-3 col-md-6 col-12">
    <div className="lead-dash-region-card">
      <div className="lead-dash-region-card-header">{region}</div>
      <div className="lead-dash-region-card-body">
        <h4 className="lead-dash-region-card-subtitle">Top 3 Narratives</h4>
        <ul className="lead-dash-region-list">
          {narratives.length > 0 ? (
            narratives.map((narrative, index) => (
              <li key={narrative}>
                <span
                  className="lead-narrative-bullet"
                  style={{
                    backgroundColor:
                      PARTNER_CHART_PALETTE[index % PARTNER_CHART_PALETTE.length],
                  }}
                />
                <span>{narrative}</span>
              </li>
            ))
          ) : (
            <li>No activity to show</li>
          )}
        </ul>
      </div>
      <div className="lead-dash-region-card-footer">
        <span className="lead-dash-region-metric-label">Avg Narrative Runs Per Partner User</span>
        <span className="lead-dash-region-metric-value">{formatAverageRuns(avgRuns)}</span>
      </div>
    </div>
  </div>
);

const PartnerKpiCard = ({
  title,
  value,
  subtitle,
  accent,
  action,
  colClass = 'col-lg-3 col-md-6 col-12',
}) => (
  <div className={colClass}>
    <div className={`lead-revamp-kpi-card lead-revamp-kpi-accent-${accent}`}>
      <h6 className="lead-revamp-kpi-label">{title}</h6>
      <div className="lead-revamp-kpi-value">{value}</div>
      {subtitle && <p className="lead-revamp-kpi-subtitle">{subtitle}</p>}
      {action}
    </div>
  </div>
);

const PartnerAnalyticsSection = ({
  period,
  isActive = true,
  selectedPartnerCompany,
  onSelectedPartnerCompanyChange,
  onPartnerCompanyOptionsChange,
  onLoadingChange,
}) => {
  const accessToken = useSelector((state) => state.auth?.accessToken);

  const [companiesModalOpen, setCompaniesModalOpen] = useState(false);
  const [activeUsersModalOpen, setActiveUsersModalOpen] = useState(false);

  const [partnerAggregateData, setPartnerAggregateData] = useState(createEmptyPartnerAnalytics());
  const [partnerCompanyData, setPartnerCompanyData] = useState(createEmptyPartnerCompanyAnalytics());

  const [partnerLoading, setPartnerLoading] = useState(true);
  const [hasLoadedPartnerData, setHasLoadedPartnerData] = useState(false);
  const [partnerError, setPartnerError] = useState('');
  const [partnerWarnings, setPartnerWarnings] = useState([]);
  const [partnerDataStatus, setPartnerDataStatus] = useState('');
  const [showAllCompanyUsers, setShowAllCompanyUsers] = useState(false);

  const [selectedRegionalBreakdown, setSelectedRegionalBreakdown] = useState('APJ');
  const [narrativeTrendSelectedCompanies, setNarrativeTrendSelectedCompanies] = useState([
    OVERALL_COMPARE_OPTION,
  ]);
  const [bookingTrendSelectedCompanies, setBookingTrendSelectedCompanies] = useState([
    OVERALL_COMPARE_OPTION,
  ]);

  const isAggregateMode = selectedPartnerCompany === ALL_PARTNER_COMPANIES;
  const filterLabel = FILTER_LABEL_MAP[period] || period;

  const narrativeSelectedCompanyComparisons = narrativeTrendSelectedCompanies.filter(
    (item) => item !== OVERALL_COMPARE_OPTION
  );
  const bookingSelectedCompanyComparisons = bookingTrendSelectedCompanies.filter(
    (item) => item !== OVERALL_COMPARE_OPTION
  );

  const compareCompaniesForRequest = useMemo(() => {
    const unique = Array.from(
      new Set([...narrativeSelectedCompanyComparisons, ...bookingSelectedCompanyComparisons])
    );
    return unique;
  }, [bookingSelectedCompanyComparisons, narrativeSelectedCompanyComparisons]);

  const compareCompaniesKey = useMemo(
    () => (isAggregateMode ? compareCompaniesForRequest.join(',') : ''),
    [compareCompaniesForRequest, isAggregateMode]
  );

  const aggregateCompanyOptions = useMemo(
    () => {
      const options = [ALL_PARTNER_COMPANIES, ...(partnerAggregateData.companyOptions || [])];
      if (
        selectedPartnerCompany !== ALL_PARTNER_COMPANIES
        && !options.includes(selectedPartnerCompany)
      ) {
        options.push(selectedPartnerCompany);
      }
      return options;
    },
    [partnerAggregateData.companyOptions, selectedPartnerCompany]
  );

  useEffect(() => {
    if (!onPartnerCompanyOptionsChange) return;
    onPartnerCompanyOptionsChange(aggregateCompanyOptions);
  }, [aggregateCompanyOptions, onPartnerCompanyOptionsChange]);

  useEffect(() => {
    setShowAllCompanyUsers(false);
    setActiveUsersModalOpen(false);
  }, [selectedPartnerCompany]);

  useEffect(() => {
    onLoadingChange?.(partnerLoading);
  }, [onLoadingChange, partnerLoading]);

  useEffect(() => {
    let isMounted = true;

    const fetchPartnerData = async () => {
      setPartnerLoading(true);
      setPartnerError('');

      if (!isActive || !accessToken) {
        if (!isMounted) return;
        setPartnerLoading(false);
        if (!accessToken) {
          setPartnerError('Authentication token missing.');
        }
        return;
      }

      try {
        const mappedPeriod = PERIOD_FILTER_MAP[period] || 'Month';

        if (isAggregateMode) {
          const response = await fetchLeadershipPartnerAnalytics(
            accessToken,
            mappedPeriod,
            PARTNER_REGION,
            compareCompaniesKey ? compareCompaniesKey.split(',') : undefined
          );

          if (!response?.success) {
            throw new Error(response?.message || 'Partner analytics API returned an unsuccessful response.');
          }

          if (!isMounted) return;
          const nextStatus = response?.meta?.dataStatus || '';
          const mappedData = mapPartnerAnalyticsResponse(response?.partnerAnalytics || {});
          setPartnerAggregateData(nextStatus === 'empty' ? createEmptyPartnerAnalytics() : mappedData);
          setPartnerWarnings(Array.isArray(response?.meta?.warnings) ? response.meta.warnings.filter(Boolean) : []);
          setPartnerDataStatus(nextStatus);
          setPartnerCompanyData(createEmptyPartnerCompanyAnalytics());
        } else {
          const response = await fetchLeadershipPartnerCompanyAnalytics(
            accessToken,
            mappedPeriod,
            selectedPartnerCompany
          );

          if (!response?.success) {
            throw new Error(response?.message || 'Partner company analytics API returned an unsuccessful response.');
          }

          if (!isMounted) return;
          const nextStatus = response?.meta?.dataStatus || '';
          const mappedData = mapPartnerCompanyAnalyticsResponse(response?.partnerCompanyAnalytics || {});
          setPartnerCompanyData(nextStatus === 'empty' ? createEmptyPartnerCompanyAnalytics() : mappedData);
          setPartnerWarnings(Array.isArray(response?.meta?.warnings) ? response.meta.warnings.filter(Boolean) : []);
          setPartnerDataStatus(nextStatus);
        }
      } catch (_error) {
        if (!isMounted) return;
        setPartnerError(API_ERROR_MESSAGE);
        setPartnerWarnings([]);
        setPartnerDataStatus('empty');
        if (isAggregateMode) {
          setPartnerAggregateData(createEmptyPartnerAnalytics());
        } else {
          setPartnerCompanyData(createEmptyPartnerCompanyAnalytics());
        }
      } finally {
        if (isMounted) {
          setPartnerLoading(false);
          setHasLoadedPartnerData(true);
        }
      }
    };

    fetchPartnerData();

    return () => {
      isMounted = false;
    };
  }, [isActive, accessToken, period, selectedPartnerCompany, compareCompaniesKey, isAggregateMode]);

  useEffect(() => {
    if (!isAggregateMode) return;
    const availableCompanyOptions = partnerAggregateData.companyOptions || [];
    const sanitizeSelection = (selection) => {
      const companiesOnly = selection.filter((item) => item !== OVERALL_COMPARE_OPTION);
      const valid = companiesOnly.filter((item) => availableCompanyOptions.includes(item));
      if (selection.includes(OVERALL_COMPARE_OPTION) || valid.length === 0) {
        return [OVERALL_COMPARE_OPTION];
      }
      return valid;
    };

    setNarrativeTrendSelectedCompanies((prev) => {
      const next = sanitizeSelection(prev);
      return areArraysEqual(prev, next) ? prev : next;
    });
    setBookingTrendSelectedCompanies((prev) => {
      const next = sanitizeSelection(prev);
      return areArraysEqual(prev, next) ? prev : next;
    });
  }, [partnerAggregateData.companyOptions, isAggregateMode]);

  const handleCompanySelectFromModal = (companyName) => {
    if (companyName) {
      onSelectedPartnerCompanyChange?.(companyName);
    }
    setCompaniesModalOpen(false);
  };

  const removeComparisonChip = (company, setSelectedValues) => {
    setSelectedValues((prev) => {
      const next = prev.filter((item) => item !== company);
      return next.length ? next : [OVERALL_COMPARE_OPTION];
    });
  };

  const buildTrendChartData = (totalData, companiesData, selectedCompanies) => {
    if (!selectedCompanies.length) return totalData;
    const fallbackXData = totalData?.xData || [];
    const xData = companiesData[selectedCompanies[0]]?.xData || fallbackXData;
    return {
      xData,
      series: selectedCompanies.map((company, index) => ({
        name: company,
        data: companiesData[company]?.data || new Array(xData.length).fill(0),
        color: PARTNER_CHART_PALETTE[index % PARTNER_CHART_PALETTE.length],
      })),
    };
  };

  const aggregateNarrativeTrendData = useMemo(() => {
    const trendData = buildTrendChartData(
      partnerAggregateData.narrativeUsageTrendTotal,
      partnerAggregateData.narrativeUsageTrendCompanies,
      narrativeSelectedCompanyComparisons
    );
    return ensureTrendChartData({
      trendData,
      period,
      fallbackXAxis: partnerAggregateData.narrativeUsageTrendTotal?.xData || [],
      seriesNames: narrativeSelectedCompanyComparisons,
    });
  }, [
    narrativeSelectedCompanyComparisons,
    partnerAggregateData.narrativeUsageTrendCompanies,
    partnerAggregateData.narrativeUsageTrendTotal,
    period,
  ]);

  const aggregateBookingTrendData = useMemo(() => {
    const trendData = buildTrendChartData(
      partnerAggregateData.bookingTrendTotal,
      partnerAggregateData.bookingTrendCompanies,
      bookingSelectedCompanyComparisons
    );
    return ensureTrendChartData({
      trendData,
      period,
      fallbackXAxis: partnerAggregateData.bookingTrendTotal?.xData || [],
      seriesNames: bookingSelectedCompanyComparisons,
    });
  }, [
    bookingSelectedCompanyComparisons,
    partnerAggregateData.bookingTrendCompanies,
    partnerAggregateData.bookingTrendTotal,
    period,
  ]);

  const companyNarrativeTrendData = useMemo(() => (
    ensureTrendChartData({
      trendData: partnerCompanyData.narrativeUsageTrend,
      period,
      fallbackXAxis: partnerAggregateData.narrativeUsageTrendTotal?.xData || [],
    })
  ), [partnerAggregateData.narrativeUsageTrendTotal, partnerCompanyData.narrativeUsageTrend, period]);

  const companyBookingTrendData = useMemo(() => (
    ensureTrendChartData({
      trendData: partnerCompanyData.bookingTrend,
      period,
      fallbackXAxis: partnerAggregateData.bookingTrendTotal?.xData || [],
    })
  ), [partnerAggregateData.bookingTrendTotal, partnerCompanyData.bookingTrend, period]);

  const selectedRegionalBreakdownData =
    partnerAggregateData.regionalBreakdownByRegion[selectedRegionalBreakdown];
  const activeUsers = partnerAggregateData?.activePartnerUsers?.users || [];
  const activeUserTotal = partnerAggregateData?.activePartnerUsers?.total || activeUsers.length || 0;

  const visibleCompanyUserNames = useMemo(() => {
    const detailRows = Array.isArray(partnerCompanyData.userDetails)
      ? partnerCompanyData.userDetails
      : [];
    const orderedNames = detailRows
      .map((item) => item?.name || item?.user || item?.email || '')
      .filter(Boolean);
    if (showAllCompanyUsers) return orderedNames;
    return orderedNames.slice(0, 6);
  }, [partnerCompanyData.userDetails, showAllCompanyUsers]);

  const hiddenCompanyUsersCount = useMemo(() => {
    const total = Array.isArray(partnerCompanyData.userDetails)
      ? partnerCompanyData.userDetails.length
      : 0;
    return Math.max(0, total - 6);
  }, [partnerCompanyData.userDetails]);

  const filteredCompanyNarrativeBreakdown = useMemo(() => {
    if (!visibleCompanyUserNames.length) return partnerCompanyData.userNarrativeRunBreakdown;
    return partnerCompanyData.userNarrativeRunBreakdown.filter((row) =>
      visibleCompanyUserNames.includes(row.name)
    );
  }, [partnerCompanyData.userNarrativeRunBreakdown, visibleCompanyUserNames]);

  const filteredCompanyBookingBreakdown = useMemo(() => {
    if (!visibleCompanyUserNames.length) return partnerCompanyData.userBookingBreakdown;
    return partnerCompanyData.userBookingBreakdown.filter((row) =>
      visibleCompanyUserNames.includes(row.name)
    );
  }, [partnerCompanyData.userBookingBreakdown, visibleCompanyUserNames]);

  if (partnerLoading && !hasLoadedPartnerData) {
    return (
      <div className="lead-dash-card lead-dash-card-full">
        <div className="lead-dash-card-body">
          <div className="lead-dash-no-data lead-partner-no-data">
            Loading Partner analytics...
          </div>
        </div>
      </div>
    );
  }

  const isCompareRefreshLoading = partnerLoading && hasLoadedPartnerData && isAggregateMode;

  return (
    <div className="lead-dash-content">
      <CompaniesModal
        isOpen={companiesModalOpen}
        onClose={() => setCompaniesModalOpen(false)}
        companies={partnerAggregateData.partnerCompanies}
        onSelectCompany={handleCompanySelectFromModal}
      />
      <ActivePartnerUsersModal
        isOpen={activeUsersModalOpen}
        onClose={() => setActiveUsersModalOpen(false)}
        users={activeUsers}
        total={activeUserTotal}
      />

      {partnerWarnings.length > 0 && (
        <div className="lead-dash-inline-warning" role="status" aria-live="polite">
          {partnerWarnings[0]}
        </div>
      )}

      {partnerError && (
        <div className="lead-dash-inline-error" role="alert">
          {partnerError}
        </div>
      )}

      {partnerDataStatus === 'empty' && !partnerError && (
        <div className="lead-dash-inline-info" role="status">
          No activity for this period.
        </div>
      )}

      {isAggregateMode ? (
        <>
          <div id="export-partner-kpi-summary" className="partner-kpi-grid">
            {[
              {
                title: 'Total Partner Companies',
                value: partnerAggregateData.kpis.partnerCompanies,
                accent: 'blue',
                action: (
                  <button
                    type="button"
                    className="lead-revamp-kpi-link"
                    onClick={() => setCompaniesModalOpen(true)}
                  >
                    View companies
                  </button>
                ),
              },
              {
                title: 'Active Partner Users',
                value: activeUserTotal,
                accent: 'green',
                action: (
                  <button
                    type="button"
                    className="lead-revamp-kpi-link"
                    onClick={() => setActiveUsersModalOpen(true)}
                  >
                    View active users
                  </button>
                ),
              },
              {
                title: 'Global Avg Narrative Runs per Partner User',
                value: formatAverageRuns(partnerAggregateData.kpis.globalAvgRunsPerPartnerUser),
                accent: 'purple',
              },
              {
                title: 'Total Partner Narrative Runs',
                value: partnerAggregateData.kpis.totalPartnerNarrativeRuns,
                accent: 'orange',
              },
              {
                title: 'Total Partner Bookings',
                value: partnerAggregateData.kpis.totalPartnerBookings,
                accent: 'blue',
              },
            ].map((kpi) => (
              <PartnerKpiCard key={kpi.title} {...kpi} colClass="partner-kpi-grid-item" />
            ))}
          </div>

          <div id="export-partner-regional-overview" className="lead-dash-card lead-dash-card-full">
            <div className="lead-dash-card-header">
              <h3 className="lead-dash-card-title">Partner Regional Overview</h3>
            </div>
            <div className="lead-dash-card-body">
              <div className="row g-3">
                {REGIONS.map((region) => (
                  <PartnerRegionCard
                    key={region}
                    region={region}
                    narratives={partnerAggregateData.regionalOverview?.[region]?.topNarratives || []}
                    avgRuns={partnerAggregateData.regionalOverview?.[region]?.avgRunsPerPartnerUser || 0}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="lead-dash-grid-2 lead-dash-grid-2-wide-left">
            <div id="export-partner-top-narratives" className="lead-dash-card">
              <div className="lead-dash-card-header">
                <h3 className="lead-dash-card-title">Top Narratives Used by Partners</h3>
              </div>
              <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body">
                <PartnerHorizontalBarChart
                  data={partnerAggregateData.topNarratives}
                  renderZeroWhenEmpty={!partnerError}
                  emptyStateTitle="No activity for this period"
                  emptyStateSubtitle="0 narrative runs found for the selected filter."
                />
              </div>
            </div>
            <div id="export-partner-usage-trend" className="lead-dash-card">
              <div className="lead-dash-card-header lead-trend-compare-header analytics-chart-header">
                <h3 className="lead-dash-card-title chart-title">Partner Narrative Usage Trend</h3>
                <div className="lead-compare-header-control">
                  <PartnerComparisonDropdown
                    selectedValues={narrativeTrendSelectedCompanies}
                    companyOptions={partnerAggregateData.companyOptions || []}
                    onApplySelection={setNarrativeTrendSelectedCompanies}
                    label="Compare"
                  />
                </div>
              </div>
              <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body partner-compare-chart-body">
                {!narrativeTrendSelectedCompanies.includes(OVERALL_COMPARE_OPTION) && (
                  <div className="lead-compare-chip-row">
                    {narrativeSelectedCompanyComparisons.map((company, index) => (
                      <button
                        key={company}
                        type="button"
                        className="lead-compare-chip analytics-compare-chip"
                        onClick={() =>
                          removeComparisonChip(company, setNarrativeTrendSelectedCompanies)
                        }
                      >
                        <span
                          className="lead-compare-chip-dot"
                          style={{
                            backgroundColor:
                              PARTNER_CHART_PALETTE[index % PARTNER_CHART_PALETTE.length],
                          }}
                        />
                        {company} <span aria-hidden="true">&times;</span>
                      </button>
                    ))}
                  </div>
                )}
                <PartnerSingleLineChart
                  data={aggregateNarrativeTrendData}
                  renderZeroWhenEmpty={!partnerError}
                  emptyStateTitle="No activity for this period"
                  emptyStateSubtitle="0 narrative runs found for the selected filter."
                />
                {isCompareRefreshLoading && (
                  <div className="lead-partner-chart-loading-overlay" role="status" aria-live="polite">
                    Updating chart...
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lead-dash-grid-2">
            <div id="export-partner-company-breakdown" className="lead-dash-card">
              <div className="lead-dash-card-header">
                <h3 className="lead-dash-card-title">Partner Company Narrative Breakdown</h3>
              </div>
              <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body">
                <PartnerStackedBarChart
                  data={partnerAggregateData.companyBreakdown}
                  renderZeroWhenEmpty={!partnerError}
                  emptyStateTitle="No activity to show"
                  emptyStateSubtitle="Try another company or time period."
                />
              </div>
            </div>
            <div id="export-partner-regional-narrative-breakdown" className="lead-dash-card">
              <div className="lead-dash-card-header">
                <h3 className="lead-dash-card-title">Partner Regional Narrative Breakdown</h3>
                <select
                  className="lead-dash-select"
                  value={selectedRegionalBreakdown}
                  onChange={(e) => setSelectedRegionalBreakdown(e.target.value)}
                  aria-label="Select partner region"
                >
                  {REGIONS.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>
              <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body">
                <PartnerStackedBarChart
                  data={selectedRegionalBreakdownData}
                  renderZeroWhenEmpty={!partnerError}
                  emptyStateTitle="No activity to show"
                  emptyStateSubtitle="Try another company or time period."
                />
              </div>
            </div>
          </div>

          <div id="export-partner-ingestion-overview" className="lead-dash-card lead-dash-card-full">
            <div className="lead-dash-card-header">
              <h3 className="lead-dash-card-title">Partner Regional Ingestion Type Overview</h3>
            </div>
            <div className="lead-dash-card-body">
              <RegionalIngestionOverview
                data={partnerAggregateData.ingestionByRegion}
                renderZeroWhenEmpty={!partnerError}
                emptyStateTitle="No ingestion activity"
                emptyStateSubtitle="0 runs found for the selected filter."
              />
            </div>
          </div>

          <div id="export-partner-booking-trend" className="lead-dash-card lead-dash-card-full">
            <div className="lead-dash-card-header lead-trend-compare-header analytics-chart-header">
              <h3 className="lead-dash-card-title chart-title">Partner Booking Volume Trend</h3>
              <div className="lead-compare-header-control">
                <PartnerComparisonDropdown
                  selectedValues={bookingTrendSelectedCompanies}
                  companyOptions={partnerAggregateData.companyOptions || []}
                  onApplySelection={setBookingTrendSelectedCompanies}
                  label="Compare"
                />
              </div>
            </div>
            <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body partner-compare-chart-body">
              {!bookingTrendSelectedCompanies.includes(OVERALL_COMPARE_OPTION) && (
                <div className="lead-compare-chip-row">
                  {bookingSelectedCompanyComparisons.map((company, index) => (
                    <button
                      key={company}
                      type="button"
                      className="lead-compare-chip analytics-compare-chip"
                      onClick={() =>
                        removeComparisonChip(company, setBookingTrendSelectedCompanies)
                      }
                    >
                      <span
                        className="lead-compare-chip-dot"
                        style={{
                          backgroundColor:
                            PARTNER_CHART_PALETTE[index % PARTNER_CHART_PALETTE.length],
                        }}
                      />
                      {company} <span aria-hidden="true">&times;</span>
                    </button>
                  ))}
                </div>
              )}
              <PartnerSingleLineChart
                data={aggregateBookingTrendData}
                yAxisLabel="Partner Bookings"
                color="#38BDF8"
                renderZeroWhenEmpty={!partnerError}
                emptyStateTitle="No bookings for this period"
                emptyStateSubtitle="0 bookings found for the selected filter."
              />
              {isCompareRefreshLoading && (
                <div className="lead-partner-chart-loading-overlay" role="status" aria-live="polite">
                  Updating chart...
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div id="export-partner-company-kpi-summary" className="row g-3">
            <PartnerKpiCard
              title="Active Partner Users"
              value={partnerCompanyData.kpis.activePartnerUsers}
              subtitle={`${selectedPartnerCompany} · ${filterLabel}`}
              accent="green"
              colClass="col-lg-3 col-md-6 col-12"
            />
            <PartnerKpiCard
              title="Total Narrative Runs"
              value={partnerCompanyData.kpis.totalNarrativeRuns}
              subtitle={`${selectedPartnerCompany} · ${filterLabel}`}
              accent="orange"
              colClass="col-lg-3 col-md-6 col-12"
            />
            <PartnerKpiCard
              title="Avg Narrative Runs per Partner User"
              value={formatAverageRuns(partnerCompanyData.kpis.avgNarrativeRunsPerPartnerUser)}
              subtitle={`${selectedPartnerCompany} · ${filterLabel}`}
              accent="purple"
              colClass="col-lg-3 col-md-6 col-12"
            />
            <PartnerKpiCard
              title="Total Bookings"
              value={partnerCompanyData.kpis.totalBookings}
              subtitle={`${selectedPartnerCompany} · ${filterLabel}`}
              accent="blue"
              colClass="col-lg-3 col-md-6 col-12"
            />
          </div>

          <div className="lead-dash-grid-2 lead-dash-grid-2-wide-left">
            <div id="export-partner-company-top-narratives" className="lead-dash-card">
              <div className="lead-dash-card-header">
                <h3 className="lead-dash-card-title">Top Narratives Used by {selectedPartnerCompany}</h3>
              </div>
              <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body">
                <PartnerHorizontalBarChart
                  data={partnerCompanyData.topNarratives}
                  renderZeroWhenEmpty={!partnerError}
                  emptyStateTitle="No activity for this period"
                  emptyStateSubtitle="0 narrative runs found for the selected filter."
                />
              </div>
            </div>
            <div id="export-partner-company-ingestion" className="lead-dash-card">
              <div className="lead-dash-card-header">
                <h3 className="lead-dash-card-title">{selectedPartnerCompany} Ingestion Type Overview</h3>
              </div>
              <div className="lead-dash-card-body">
                <div className="lead-compact-donut-wrapper">
                  <CompactDonutChart
                    data={partnerCompanyData.ingestion}
                    compact
                    colorPalette={PARTNER_BLUE_COLORS}
                    renderZeroWhenEmpty={!partnerError}
                    emptyStateTitle="No ingestion activity"
                    emptyStateSubtitle="0 runs found for the selected filter."
                    emptyStateCompact
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="lead-dash-grid-2">
            <div id="export-partner-company-narrative-trend" className="lead-dash-card">
              <div className="lead-dash-card-header">
                <h3 className="lead-dash-card-title">{selectedPartnerCompany} Narrative Usage Trend</h3>
              </div>
              <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body">
                <PartnerSingleLineChart
                  data={companyNarrativeTrendData}
                  renderZeroWhenEmpty={!partnerError}
                  emptyStateTitle="No activity for this period"
                  emptyStateSubtitle="0 narrative runs found for the selected filter."
                />
              </div>
            </div>
            <div id="export-partner-company-booking-trend" className="lead-dash-card">
              <div className="lead-dash-card-header">
                <h3 className="lead-dash-card-title">{selectedPartnerCompany} Booking Volume Trend</h3>
              </div>
              <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body">
                <PartnerSingleLineChart
                  data={companyBookingTrendData}
                  yAxisLabel="Partner Bookings"
                  color="#38BDF8"
                  renderZeroWhenEmpty={!partnerError}
                  emptyStateTitle="No bookings for this period"
                  emptyStateSubtitle="0 bookings found for the selected filter."
                />
              </div>
            </div>
          </div>

          <div className="lead-dash-section-header">
            <h3 className="lead-dash-section-title">{selectedPartnerCompany.toUpperCase()} USER ACTIVITY BREAKDOWN</h3>
            {!showAllCompanyUsers && hiddenCompanyUsersCount > 0 && (
              <button
                type="button"
                className="lead-revamp-kpi-link"
                onClick={() => setShowAllCompanyUsers(true)}
              >
                View user details (+{hiddenCompanyUsersCount} more) &#9662;
              </button>
            )}
            {showAllCompanyUsers && (
              <button
                type="button"
                className="lead-revamp-kpi-link"
                onClick={() => setShowAllCompanyUsers(false)}
              >
                Show fewer users &#9652;
              </button>
            )}
          </div>

          <div className="lead-dash-grid-2">
            <div id="export-partner-company-user-narrative-breakdown" className="lead-dash-card">
              <div className="lead-dash-card-header">
                <h3 className="lead-dash-card-title">{selectedPartnerCompany} User Narrative Run Breakdown</h3>
              </div>
              <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body">
                <UserNarrativeStackedChart
                  data={filteredCompanyNarrativeBreakdown}
                  renderZeroWhenEmpty={!partnerError}
                  emptyStateTitle="No activity to show"
                  emptyStateSubtitle="Try another company or time period."
                />
              </div>
            </div>
            <div id="export-partner-company-user-booking-breakdown" className="lead-dash-card">
              <div className="lead-dash-card-header">
                <h3 className="lead-dash-card-title">{selectedPartnerCompany} User Booking Breakdown</h3>
              </div>
              <div className="lead-dash-card-body lead-dash-chart-body partner-chart-body">
                <SimpleVerticalBarChart
                  data={filteredCompanyBookingBreakdown}
                  renderZeroWhenEmpty={!partnerError}
                  emptyStateTitle="No bookings for this period"
                  emptyStateSubtitle="0 bookings found for the selected filter."
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PartnerAnalyticsSection;


